import React from 'react';
import PropTypes from 'prop-types';

import { i18n } from 'config/locales';
// eslint-disable-next-line import/no-cycle
import { newGuid } from '../ValidationElement';
import { scrollToBottom as scrollToBottomFn } from '../Accordion/Accordion';
import { setAttribute } from '../shared/setAttribute';
import Branch from '../Branch';

export default class BranchCollection extends React.Component {
  constructor(props) {
    super(props);
    this.content = this.content.bind(this);
  }

  componentDidMount() {
    if (this.props.hideInitialBranchQuestionAssumeYes && ((this.props?.items || []).length === 0)) {
      const firstItem = { name: 'branchCollection', value: 'Yes' };
      this.addFirstItem(firstItem);
    }
  }

  componentDidUpdate() {
    if (this.props.hideInitialBranchQuestionAssumeYes && ((this.props?.items || []).length === 0)) {
      const firstItem = { name: 'branchCollection', value: 'Yes' };
      this.addFirstItem(firstItem);
    }
  }

  /**
   * Updates existing selected branch values
   */
  onBranchClick(item, index, values) {
    const {
      items, valueKey, removable, onUpdate
    } = this.props;

    // TODO: This only provides a shallow copy; need to use lodash cloneDeep instead
    const newItems = [...items];
    if (values.value === 'Yes') {
      if (!newItems[index].Item) {
        newItems[index].Item = {};
      }
      newItems[index].Item = {
        ...newItems[index].Item,
        [valueKey]: values,
      };
    } else {
      const isLastItem = index + 1 >= items.length;
      if (!isLastItem && removable) {
        // If it's not the last item being marked as No, then remove it. This addresses the issue
        // where the user must click No twice on the last item.
        newItems.splice(index, 1);
      } else {
        // clear out any persisted data **except** the branch value.
        newItems[index] = {
          Item: {
            [valueKey]: values,
          },
          index: item.index,
        };
      }
    }

    onUpdate({ items: newItems });
  }

  addFirstItem = (values) => {
    const { valueKey, onUpdate } = this.props;

    const item = {
      Item: { [valueKey]: values },
      index: newGuid(),
    };

    const items = [item];
    onUpdate({ items });
  };

  /**
   * Used when populating branch values for the first time
   */
  onDefaultBranchClick(values) {
    this.addFirstItem(values);
  }

  /**
   * Used when a user decides to add new information. This refers to the last
   * displayed yes/no branch
   */
  onLastBranchClick(values) {
    const {
      items, valueKey, scrollToBottom, onUpdate
    } = this.props;

    const item = {
      Item: { [valueKey]: values },
      index: newGuid(),
    };

    if (values.value === 'Yes') {
      const newItems = [...items];
      newItems.push(item);
      onUpdate({ items: newItems });
    } else {
      const newItems = [...items];
      newItems.push(item);
      if (scrollToBottom) {
        scrollToBottomFn(scrollToBottom);
      }
      onUpdate({ items: newItems });
    }
  }

  recursiveCloneChildren(children, item, index) {
    return React.Children.map(children, (child) => {
      let childProps = {};
      if (React.isValidElement(child)) {
        if (child.props.bind) {
          const {
            items, onUpdate, onError, errors
          } = this.props;

          const field = item[child.props.name];
          childProps = { ...field, $index: index };
          childProps.onUpdate = (value) => {
            const newItems = [...items];
            const newItem = newItems[index][child.props.name];
            newItems[index][child.props.name] = {
              ...newItem,
              ...value,
            };
            onUpdate({ items: newItems });
          };
          childProps.onError = onError;

          // Add errors to child component
          const childErrors = errors && errors.filter((e) => e.indexOf(item.index) > -1);
          if (childErrors && childErrors.length) { childProps.errors = childErrors; }
        }
      }

      const typeOfChildren = Object.prototype.toString.call(child.props.children);
      if (child.props.children && ['[object Object]', '[object Array]'].includes(typeOfChildren)) {
        childProps.children = this.recursiveCloneChildren(child.props.children, item, index);
      }

      return React.cloneElement(child, childProps);
    });
  }

  /**
   * Helper that renders branch information. Allows props to be overriden
   */
  branch(props) {
    const {
      required, scrollIntoView, useNoLabel, noLabel, useYesLabel, yesLabel
    } = this.props;

    const useSecondaryAppendLabels = this.props.hasSecondaryAppendLabels && (props.index > 0);
    const noAppendLabel = useSecondaryAppendLabels ? this.props.secondaryNoAppendLabel : noLabel;
    const yesAppendLabel = useSecondaryAppendLabels ? this.props.secondaryYesAppendLabel : yesLabel;

    return (
      <>
        <Branch
          attribute={setAttribute(this.props.attribute, `items[${props.index}].Item.Has`)}
          name={props.name}
          label={props.label}
          labelSize={props.labelSize}
          className={props.className}
          help={props.help}
          {...(props.value || {})}
          warning={props.warning}
          onUpdate={props.onUpdate}
          required={required}
          onError={props.onError}
          scrollIntoView={scrollIntoView}
          dnkLabel={props.dnkLabel}
          showDNK={props.showDNK}
          exactAttribute={props.exactAttribute}
          {...(useNoLabel ? { noLabel: noAppendLabel } : {})}
          {...(useYesLabel ? { yesLabel: yesAppendLabel } : {})}
        >
          {props.children}
        </Branch>
        {props.branchComment?.()}
      </>
    );
  }

  content() {
    const {
      items,
      valueKey,
      branchName,
      label,
      labelSize,
      help,
      content,
      onError,
      branchClassName,
      appendLabel,
      appendSize,
      appendContent,
      children,
      branchComment,
      dnkLabel,
      showDNK,
      exactAttribute
    } = this.props;

    const renderItems = (items || []).map((item) => {
      if (!item.index) {
        // eslint-disable-next-line no-param-reassign
        item.index = newGuid();
      }
      return item;
    });

    const hasNo = !!renderItems.find((item) => ((item.Item || {})[valueKey] || {}).value !== 'Yes');

    // When no items are present, render default branch yes/no
    if (renderItems.length === 0) {
      if (this.props.hideInitialBranchQuestionAssumeYes) {
        // the add should be triggered on Component Mount
        return null;
      }
      if (this.props.hideAppend) {
        return null;
      }

      return (
        <div>
          {this.branch({
            index: 0,
            name: branchName,
            label,
            labelSize,
            help,
            value: {},
            warning: false,
            children: content,
            onUpdate: this.onDefaultBranchClick.bind(this),
            onError,
            branchComment,
            dnkLabel,
            showDNK
          })}
        </div>
      );
    }

    // If a branch has been selected but it has a `No` value, rather than deleting, we'll update
    // its value
    if (renderItems.length === 1 && ((renderItems[0].Item || {})[valueKey] || {}).value === 'No') {
      const [item] = items;
      return (
        <div key={item.index}>
          {this.branch({
            index: 0,
            name: branchName,
            label,
            labelSize,
            help,
            value: { value: 'No' },
            warning: false,
            children: content,
            onUpdate: this.onBranchClick.bind(this, item, 0),
            onError,
            branchComment,
            dnkLabel,
            showDNK,
            exactAttribute
          })}
        </div>
      );
    }

    // When more than 1 item is in
    const top = (index, item) => {
      const className = ((item.Item || {})[valueKey] || {}).value === 'Yes' ? branchClassName : null;

      if (index === 0) {
        if (this.props.hideInitialBranchQuestionAssumeYes) {
          return null;
        }

        return this.branch({
          index,
          name: branchName,
          label,
          labelSize,
          className,
          value: (item.Item || {})[valueKey],
          warning: true,
          help,
          children: content,
          onUpdate: this.onBranchClick.bind(this, item, index),
          onError,
          branchComment,
          dnkLabel,
          showDNK,
          exactAttribute
        });
      }

      if (this.props.hideAppend) {
        return null;
      }
      return this.branch({
        index,
        name: branchName,
        label: appendLabel,
        labelSize: appendSize,
        className,
        help,
        value: (item.Item || {})[valueKey],
        warning: true,
        children: appendContent,
        onUpdate: this.onBranchClick.bind(this, item, index),
        onError,
        dnkLabel,
        showDNK
      });
    };

    // Render the branch question at the very end
    const bottom = (index, item, arr) => {
      if (index < arr.length - 1 || hasNo || this.props.hideAppend) {
        return null;
      }

      return this.branch({
        index: index + 1,
        name: branchName,
        className: 'last-branch',
        label: appendLabel,
        labelSize: appendSize,
        help,
        onUpdate: this.onLastBranchClick.bind(this),
        children: appendContent,
        value: {},
        warning: false,
        dnkLabel,
        showDNK,
        exactAttribute
      });
    };

    const kiddos = (index, item) => {
      const key = (item.Item || {})[valueKey] || {};
      return key.value === 'Yes' ? this.recursiveCloneChildren(children, item, index) : null;
    };

    const rows = renderItems.map((item, index, arr) => (
      <div key={item.index}>
        {top(index, item)}
        <div>{kiddos(index, item)}</div>
        {bottom(index, item, arr)}
      </div>
    ));

    return <div>{rows}</div>;
  }

  render() {
    const { className } = this.props;
    return <div className={className}>{this.content()}</div>;
  }
}

BranchCollection.propTypes = {
  attribute: PropTypes.string,
  hideInitialBranchQuestionAssumeYes: PropTypes.bool,
  items: PropTypes.array,
  removable: PropTypes.bool,
  branchName: PropTypes.string,
  help: PropTypes.string,
  valueKey: PropTypes.string,
  label: PropTypes.string,
  labelSize: PropTypes.string,
  content: PropTypes.node,
  appendLabel: PropTypes.string,
  appendSize: PropTypes.string,
  appendContent: PropTypes.node,
  onUpdate: PropTypes.func,
  scrollToBottom: PropTypes.string,
  branchClassName: PropTypes.string,
  required: PropTypes.bool,
  scrollIntoView: PropTypes.bool,
  onError: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
  errors: PropTypes.array,
  branchComment: PropTypes.func,
  dnkLabel: PropTypes.string,
  showDNK: PropTypes.bool,
  useNoLabel: PropTypes.bool,
  noLabel: PropTypes.string,
  useYesLabel: PropTypes.bool,
  yesLabel: PropTypes.string,
  hasSecondaryAppendLabels: PropTypes.bool,
  secondaryNoAppendLabel: PropTypes.string,
  secondaryYesAppendLabel: PropTypes.string,
  exactAttribute: PropTypes.bool,
  hideAppend: PropTypes.bool,
};

BranchCollection.defaultProps = {
  // Items in the collection to render
  items: [],

  // If selecting No removes the item
  removable: true,

  // Input name for the supporting Branch component
  branchName: 'branchcollection',

  // Branch help id
  help: '',

  hideInitialBranchQuestionAssumeYes: false,

  // Key name that stores whether yes/no has been selected
  valueKey: 'Has',

  label: '',
  labelSize: 'h4',
  content: null,
  appendLabel: '',
  appendSize: 'h4',
  appendContent: null,

  onUpdate: () => {
    console.warn(
      'onUpdate function not provided in BranchCollection. Please add one or your updates will not work'
    );
  },
  scrollToBottom: '',
  branchClassName: '',
  required: false,
  scrollIntoView: false,
  dnkLabel: i18n.t('branch.label.dnk'),
  showDNK: false,
  onError: () => { },
  children: null,
  className: undefined,
  errors: [],
  attribute: '',
  useNoLabel: false,
  noLabel: 'No',
  useYesLabel: false,
  yesLabel: 'Yes',
  hasSecondaryAppendLabels: false,
  secondaryNoAppendLabel: 'No',
  secondaryYesAppendLabel: 'Yes',
  branchComment: () => { },
  exactAttribute: false,
  hideAppend: false,
};

BranchCollection.errors = [];
